import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FloatingField } from '@/components/ui/floating-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { StatusPill } from '@/components/ui/status-pill';
import { formatCurrency } from '@/lib/utils';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const PAYMENT_METHODS = [
  { value: 'ach', label: 'ACH' },
  { value: 'check', label: 'Check' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'card', label: 'Credit Card' },
  { value: 'other', label: 'Other' },
];

const BUCKET_META = [
  { key: 'current', label: 'Current', color: 'text-green-500' },
  { key: '1_30', label: '1-30 days', color: 'text-blue-500' },
  { key: '31_60', label: '31-60 days', color: 'text-yellow-500' },
  { key: '61_90', label: '61-90 days', color: 'text-orange-500' },
  { key: '90_plus', label: '90+ days', color: 'text-red-500' },
];

export default function ARPage() {
  const qc = useQueryClient();
  const [showApply, setShowApply] = useState(false);
  const [form, setForm] = useState({ invoice: '', amount: '', method: 'ach', reference: '' });

  const dataQ = useQuery({
    queryKey: ['ar-page'],
    refetchOnMount: 'always',
    queryFn: async () => {
      const [a, inv] = await Promise.all([
        api('/api/v1/ar/aging'),
        api('/api/v1/invoices'),
      ]);
      if (a.status !== 200) throw new Error(a.data?.error ?? 'Failed to load aging');
      if (inv.status !== 200) throw new Error(inv.data?.error ?? 'Failed to load invoices');
      return {
        rows: a.data.rows ?? [],
        totals: a.data.totals ?? {},
        invoices: (inv.data.invoices ?? []).filter((i) => {
          if (i.status === 'void' || i.status === 'paid') return false;
          const balance = (i.amountCents ?? 0) - (i.amountPaidCents ?? 0);
          return balance > 0;
        }),
      };
    },
  });

  useEffect(() => {
    if (dataQ.isError) toast.error(dataQ.error?.message ?? 'Failed to load AR');
  }, [dataQ.isError, dataQ.error]);

  const rows = dataQ.data?.rows ?? [];
  const totals = dataQ.data?.totals ?? {};
  const invoices = dataQ.data?.invoices ?? [];

  const handleApply = async () => {
    const amountCents = Math.round((parseFloat(form.amount) || 0) * 100);
    if (!form.invoice || amountCents <= 0) {
      toast.error('Select invoice and amount');
      return;
    }
    if (!form.method) {
      toast.error('Payment method required');
      return;
    }
    if (!form.reference?.trim()) {
      toast.error('Reference / check number is required');
      return;
    }
    const r = await api('/api/v1/ar/payments', {
      method: 'POST',
      body: {
        invoiceId: form.invoice,
        amountCents,
        method: form.method,
        reference: form.reference.trim(),
      },
    });
    if (r.status === 201) {
      toast.success('Payment recorded');
      setForm({ invoice: '', amount: '', method: 'ach', reference: '' });
      setShowApply(false);
      qc.invalidateQueries({ queryKey: ['ar-page'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const columns = [
    { key: 'invoiceNumber', label: 'Invoice', className: 'font-mono' },
    { key: 'billToName', label: 'Customer', sortable: true },
    {
      key: 'balanceCents',
      label: 'Amount',
      render: (v) => <span className="font-mono">{formatCurrency((v || 0) / 100)}</span>,
    },
    { key: 'daysPastDue', label: 'Days Old', className: 'font-mono' },
    {
      key: 'bucket',
      label: 'Bucket',
      render: (v) => <StatusPill status={v} />,
    },
  ];

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Accounts Receivable"
          description="Track outstanding invoices and apply payments"
          icon="Landmark"
          metrics={[
            { label: 'Open', value: rows.length },
            {
              label: 'Current',
              value: formatCurrency((totals.current ?? 0) / 100),
            },
            {
              label: '90+',
              value: formatCurrency((totals['90_plus'] ?? 0) / 100),
            },
          ]}
          actions={
            <Button onClick={() => setShowApply(!showApply)}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              Apply Payment
            </Button>
          }
        />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {BUCKET_META.map((bucket) => (
            <Card key={bucket.key}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground font-normal">
                  {bucket.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={`text-2xl font-bold font-mono ${bucket.color}`}>
                  {formatCurrency((totals[bucket.key] ?? 0) / 100)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {showApply && (
          <Card>
            <CardHeader>
              <CardTitle>Apply Payment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Invoice *
                  </label>
                  <Combobox
                    options={invoices.map((i) => ({
                      value: i.id,
                      label: `${i.invoiceNumber} — ${i.billToName} ($${(
                        (i.amountCents - (i.amountPaidCents ?? 0)) /
                        100
                      ).toFixed(2)} due)`,
                    }))}
                    value={form.invoice}
                    onChange={(v) => setForm({ ...form, invoice: v })}
                    placeholder="Select invoice"
                  />
                </div>
                <FloatingField
                  label="Amount ($)"
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                />
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Payment Method *
                  </label>
                  <Select
                    value={form.method}
                    onValueChange={(v) => setForm({ ...form, method: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <FloatingField
                  label="Reference / Check #"
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowApply(false)}>
                  Cancel
                </Button>
                <Button onClick={handleApply}>Apply Payment</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <DataTable
          columns={columns}
          data={rows}
          emptyMessage={
            dataQ.isLoading
              ? 'Loading…'
              : 'No outstanding invoices'
          }
        />
      </div>
);
}
